import type { ClassificationReviewItem, ReviewOrdering } from "../types/review";

/**
 * In-page event-grouping pass for the review grid.
 *
 * The actual ordering is applied by the BACKEND via the `ordering` query param
 * (see {@link ReviewFilters.ordering}) so it spans the full filtered result set
 * before pagination. This helper runs as a light secondary pass on the items
 * already loaded, purely to keep an event's frames visually contiguous.
 *
 * EVENT-AWARE: the grid groups *consecutive* same-event frames (dashed
 * borders + the one-event-per-row layout). When concurrent events interleave
 * in time the backend order alone can scatter an event's frames apart. So we
 * re-group at the EVENT level: every frame of an event stays contiguous (in
 * `event_frame` order) and events are ranked — consistently with the backend
 * ordering — by the chosen criterion applied across their frames.
 */

/**
 * The orderings the backend actually supports, in the order the Sort control
 * shows them. Deliberately closed: `storagekey` (Filename) and ascending
 * `reviewed_at` are NOT backend orderings, so offering them would silently
 * degrade to the default sort on the server.
 */
export const REVIEW_ORDERING_OPTIONS: ReadonlyArray<{
  value: ReviewOrdering;
  /** Ordering values are wire params; only the segment text is translated. */
  labelKey: "ordering.newest" | "ordering.oldest" | "ordering.recentlyReviewed";
}> = [
  { value: "-timestamp", labelKey: "ordering.newest" },
  { value: "timestamp", labelKey: "ordering.oldest" },
  { value: "-reviewed_at", labelKey: "ordering.recentlyReviewed" },
];

export const DEFAULT_REVIEW_ORDERING: ReviewOrdering = "-timestamp";

/** Most-recent `reviewed_at` across an item's labels, as epoch ms (0 if none). */
const latestReviewedAt = (item: ClassificationReviewItem): number => {
  let latest = 0;
  for (const label of item.labels ?? []) {
    if (label.reviewed_at) {
      const t = new Date(label.reviewed_at).getTime();
      if (Number.isFinite(t) && t > latest) latest = t;
    }
  }
  return latest;
};

const ts = (item: ClassificationReviewItem): number =>
  new Date(item.timestamp).getTime();

/** The per-frame value used to rank an event for a given ordering. */
const frameRankValue = (
  item: ClassificationReviewItem,
  ordering: string | undefined,
): number | string => {
  switch (ordering) {
    case "-reviewed_at":
      return latestReviewedAt(item);
    case "timestamp":
    case "-timestamp":
    default:
      return ts(item);
  }
};

/** Smaller rank values sort first for Oldest; larger first otherwise. */
const isAscending = (ordering: string | undefined): boolean =>
  ordering === "timestamp";

const compareRanks = (a: number | string, b: number | string): number =>
  typeof a === "string" || typeof b === "string"
    ? String(a).localeCompare(String(b))
    : a - b;

/**
 * One representative rank per `event_uuid`: the MIN frame value for ascending
 * orderings (Oldest/Filename), the MAX for descending ones (Newest/Updated).
 * This is what lets an entire event sort as a single block.
 */
const buildEventRanks = (
  items: ReadonlyArray<ClassificationReviewItem>,
  ordering: string | undefined,
): Map<string, number | string> => {
  const asc = isAscending(ordering);
  const ranks = new Map<string, number | string>();
  for (const it of items) {
    const value = frameRankValue(it, ordering);
    const current = ranks.get(it.event_uuid);
    if (current === undefined) {
      ranks.set(it.event_uuid, value);
      continue;
    }
    const cmp = compareRanks(value, current);
    // Keep the min for ascending orderings, the max for descending.
    if (asc ? cmp < 0 : cmp > 0) ranks.set(it.event_uuid, value);
  }
  return ranks;
};

/**
 * Return a new array of `items` sorted by `ordering`, event-aware (see the
 * file header). Unknown/undefined ordering falls back to Newest. Does not
 * mutate the input.
 */
export const sortReviewItems = <T extends ClassificationReviewItem>(
  items: T[],
  ordering: string | undefined,
): T[] => {
  const ranks = buildEventRanks(items, ordering);
  const dir = isAscending(ordering) ? 1 : -1;

  return items.slice().sort((a, b) => {
    // Frames of the same event always stay together, in natural frame order.
    if (a.event_uuid === b.event_uuid) {
      return (a.event_frame ?? 0) - (b.event_frame ?? 0);
    }
    const ra = ranks.get(a.event_uuid) ?? 0;
    const rb = ranks.get(b.event_uuid) ?? 0;
    const cmp = compareRanks(ra, rb);
    if (cmp !== 0) return dir * cmp;
    // Same rank, different events — deterministic tiebreak so the two events'
    // frames never interleave.
    return a.event_uuid.localeCompare(b.event_uuid);
  });
};
